import React from 'react';
import { AgendaItem } from '../types';

interface AgendaItemCardProps {
  item: AgendaItem;
  isLast: boolean;
  onClick: (item: AgendaItem) => void;
  isActivityGroupRegistered?: boolean;
  isArbeitsgruppeRegistered?: boolean;
}

const ClockIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const UserIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const LocationIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const ChevronRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

const formatTime = (dateString: string | null) => {
  if (!dateString) return null;
  return new Date(dateString).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const AgendaItemCard: React.FC<AgendaItemCardProps> = ({ item, isLast, onClick, isActivityGroupRegistered, isArbeitsgruppeRegistered }) => {
  const startTime = formatTime(item.start);
  const endTime = item.end ? formatTime(item.end) : null;
  const hasContent = !!item.content;
  const isAnschliessend = !item.start;
  const isArbeitsgruppen = item.type === 'arbeitsgruppen';
  const isActivityGroups = item.type === 'activity_groups';
  const isClickable = hasContent || isArbeitsgruppen || isActivityGroups || isActivityGroupRegistered;

  // Simple sanitizer to remove script tags and on* attributes
  const sanitizeHTML = (html: string) => {
    if (typeof DOMParser === 'undefined' || !html) return '';
    const doc = new DOMParser().parseFromString(html, 'text/html');

    doc.querySelectorAll('script, style').forEach(el => el.remove());

    doc.querySelectorAll('*').forEach(el => {
      for (const attr of Array.from(el.attributes)) {
        if (attr.name.startsWith('on')) {
          el.removeAttribute(attr.name);
        }
      }
    });

    return doc.body.innerHTML;
  };

  const safeDescription = item.description ? sanitizeHTML(item.description) : '';
  const safeTitle = item.title ? sanitizeHTML(item.title) : '';

  const cardContent = (
    <div className={`ml-10 flex-1 bg-white rounded-lg shadow-md ${hasContent ? 'hover:shadow-xl' : ''} transition-shadow duration-300 overflow-hidden border border-gray-200`}>
      <div className="p-5 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3 text-sm md:text-base text-gray-600 gap-2">
          <div className="flex flex-wrap items-center gap-x-1 gap-y-2">
            {!isAnschliessend && startTime && (
              <div className="flex items-center">
                <ClockIcon className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0" />
                <span className="font-semibold text-primary">
                  {endTime ? `${startTime}-${endTime} Uhr` : `${startTime} Uhr`}
                </span>
              </div>
            )}
            {isAnschliessend && (
              <div className="flex items-center">
                <ClockIcon className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0" />
                <span className="font-semibold text-primary">anschließend</span>
              </div>
            )}
            {item.speaker && (
              <div className="flex items-center">
                <UserIcon className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0" />
                <span>{item.speaker}</span>
              </div>
            )}
            {item.place && (
              <div className="flex items-center">
                <LocationIcon className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0" />
                <span>{item.place}</span>
              </div>
            )}
            {isArbeitsgruppeRegistered && (
              <div className="flex items-center text-green-700 font-medium py-1 leading-tight">
                <span className="material-symbols-outlined text-xs md:text-sm mr-2 leading-none align-middle" aria-hidden="true">person</span>
                <span>Angemeldet</span>
              </div>
            )}
            {isActivityGroupRegistered && (
              <div className="flex items-center text-green-700 font-medium py-1 leading-tight">
                <span className="material-symbols-outlined text-xs md:text-sm mr-2 leading-none align-middle" aria-hidden="true">person</span>
                <span>Angemeldet</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-between items-start gap-2">
          <h3
            className="flex-1 text-lg md:text-xl font-semibold text-gray-900 mb-3 prose prose-sm md:prose-base max-w-none prose-img:rounded-lg prose-img:max-w-full"
            dangerouslySetInnerHTML={{ __html: safeTitle }}
          />
          {(hasContent || isActivityGroupRegistered || isArbeitsgruppen || isActivityGroups) && (
            <ChevronRightIcon className="w-6 h-6 text-gray-400 mt-1 flex-shrink-0" />
          )}
        </div>

        {safeDescription && (
          <div
            className="prose prose-sm md:prose-base max-w-none text-gray-600 prose-img:rounded-lg prose-img:max-w-full"
            dangerouslySetInnerHTML={{ __html: safeDescription }}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className={`relative ${isLast ? '' : 'pb-8'}`}>
      {/* Timeline Dot */}
      <div className="absolute left-5 top-5 -ml-2 h-4 w-4 rounded-full bg-primary border-4 border-gray-50 z-10"></div>

      {/* Content Section */}
      {isClickable ? (
        <button
          onClick={() => onClick(item)}
          className="w-full text-left"
          aria-label={`Details anzeigen für ${item.title}`}
        >
          {cardContent}
        </button>
      ) : (
        <div>{cardContent}</div>
      )}
    </div>
  );
};

export default AgendaItemCard;
